import { PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';

// Official SPL Memo v2 program — stable on every Solana cluster.
export const MEMO_PROGRAM_ID = new PublicKey(
  'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'
);

// Deployed Palment vault program (Solana devnet).
// Attached as a read-only reference key on every memo tx so Solscan groups
// all Palment activity under this program's address without requiring a
// full CPI yet.
export const PALMENT_PROGRAM_ID = new PublicKey(
  '3FSWPbpQCP95j4hGRxc4kSAwsFeZhugpuzjChUMni3Mr'
);

/**
 * Build + send + await confirmation for a single-instruction Memo transaction.
 *
 * ⚠️ The SPL Memo v2 program requires every account in its instruction to
 *    be a signer (`MissingRequiredSignature` otherwise). So we include ONLY
 *    the fee payer as signer — discoverability on explorers is handled by
 *    the `PALMENT:` prefix baked into the memo text itself.
 *
 * Returns the base58 signature on success. Throws on wallet rejection or
 * confirmation failure so callers can surface a toast.
 */
export async function sendMemoTx({ connection, wallet, memo }) {
  if (!wallet?.publicKey || !wallet?.sendTransaction) {
    throw new Error('Wallet is not connected or cannot sign transactions.');
  }

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  const tx = new Transaction({
    feePayer: wallet.publicKey,
    recentBlockhash: blockhash,
  }).add(
    new TransactionInstruction({
      keys: [{ pubkey: wallet.publicKey, isSigner: true, isWritable: true }],
      programId: MEMO_PROGRAM_ID,
      data: Buffer.from(memo, 'utf-8'),
    })
  );

  let signature;
  try {
    signature = await wallet.sendTransaction(tx, connection);
  } catch (err) {
    // Re-throw with a cleaner message the UI can pattern-match on.
    console.error('[palment] wallet.sendTransaction failed', err);
    throw err;
  }

  const result = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'confirmed'
  );

  if (result.value?.err) {
    console.error('[palment] on-chain error for', signature, result.value.err);
    throw new Error(`Transaction failed on-chain: ${JSON.stringify(result.value.err)}`);
  }

  return signature;
}

/**
 * Build the Solscan URL for a given tx signature. Cluster defaults to devnet.
 */
export function solscanTxUrl(signature, cluster = 'devnet') {
  return `https://solscan.io/tx/${signature}?cluster=${cluster}`;
}

/**
 * Build the Solscan URL for the Palment program itself.
 */
export function solscanProgramUrl(cluster = 'devnet') {
  return `https://solscan.io/account/${PALMENT_PROGRAM_ID.toBase58()}?cluster=${cluster}`;
}
